import { makeAutoObservable } from 'mobx';
import { CreateCustomExerciseRequest, CustomExercise } from 'hevy-shared';
import API from 'utils/API';
import exerciseData from 'exercises/exerciseData.json';
import { captureException } from '@sentry/nextjs';
import { BundledExercise, ExerciseTemplate } from 'exercises/types';

const CUSTOM_EXERCISES_LOCAL_STORAGE_KEY = 'CUSTOM_EXERCISES_LOCAL_STORAGE_KEY';

export class ExerciseTemplates {
  private _customExercises: CustomExercise[] = [];
  private _bundledExercises: BundledExercise[] = [];

  constructor() {
    makeAutoObservable(this);
    this._bundledExercises = Object.values(exerciseData) as BundledExercise[];
  }

  get exercises(): ExerciseTemplate[] {
    const allExercises = [...this._customExercises, ...this._bundledExercises];
    return allExercises
      .filter(e => e.is_archived === false)
      .sort(function (a, b) {
        const textA = a.title.toUpperCase();
        const textB = b.title.toUpperCase();
        return textA < textB ? -1 : textA > textB ? 1 : 0;
      });
  }

  get bundledTemplates(): BundledExercise[] {
    return this._bundledExercises;
  }

  get customExercises(): CustomExercise[] {
    return this._customExercises;
  }

  createCustomExercise = async (exercise: CreateCustomExerciseRequest) => {
    const response = await API.postCustomExerciseTemplate(exercise);

    this._customExercises.push({
      id: response.data.id,
      title: exercise.title,
      priority: 10,
      muscle_group: exercise.muscle_group,
      other_muscles: exercise.other_muscles,
      exercise_type: exercise.exercise_type,
      equipment_category: exercise.equipment_category,
      is_custom: true,
      is_archived: false,
    });
  };

  getExercise = (templateId: string): ExerciseTemplate | undefined => {
    const maybeBundledExercise = this._bundledExercises.find(e => e.id === templateId);

    return maybeBundledExercise
      ? maybeBundledExercise
      : this._customExercises.find(e => e.id === templateId);
  };

  hydrate = () => {
    const customExercisesJson = window.localStorage.getItem(CUSTOM_EXERCISES_LOCAL_STORAGE_KEY);
    if (customExercisesJson) {
      this._customExercises = JSON.parse(customExercisesJson);

      window.localStorage.setItem(
        CUSTOM_EXERCISES_LOCAL_STORAGE_KEY,
        JSON.stringify(this._customExercises),
      );
    }
  };

  clearData = () => {
    this._customExercises = [];
    window.localStorage.removeItem(CUSTOM_EXERCISES_LOCAL_STORAGE_KEY);
  };

  isFetching = false;
  hasFetched = false;
  fetch = async () => {
    if (this.isFetching) {
      return;
    }
    this.isFetching = true;
    try {
      const result = await API.getCustomExerciseTemplates();
      this._customExercises = result.data;
      window.localStorage.setItem(
        CUSTOM_EXERCISES_LOCAL_STORAGE_KEY,
        JSON.stringify(this._customExercises),
      );
    } catch (error) {
      captureException(error);
      throw error;
    } finally {
      this.hasFetched = true;
      this.isFetching = false;
    }
  };
}
